import { apiClient } from "./client";
import type { Organization, OrganizationKind, OrganizationStatus, Page } from "./types";

export type ListOrganizationsParams = {
  q?: string;
  status?: OrganizationStatus;
  /** GOD sees every tenant's organisations unless narrowed to one. */
  tenant_id?: string;
  limit?: number;
  offset?: number;
};

export async function listOrganizations(
  params: ListOrganizationsParams = {},
): Promise<Page<Organization>> {
  const { data } = await apiClient.get<Page<Organization>>("/api/v1/organizations", { params });
  return data;
}

export async function getOrganization(id: string): Promise<Organization> {
  const { data } = await apiClient.get<Organization>(`/api/v1/organizations/${id}`);
  return data;
}

export type CreateOrganizationPayload = {
  code: string;
  name: string;
  kind: OrganizationKind;
  parent_id?: string | null;
  /** GOD only — GOD accounts hold no tenant, so the target must be named. */
  tenant_id?: string | null;
};

export async function createOrganization(p: CreateOrganizationPayload): Promise<Organization> {
  const { data } = await apiClient.post<Organization>("/api/v1/organizations", p);
  return data;
}

export type UpdateOrganizationPayload = Partial<{
  name: string;
  kind: OrganizationKind;
  parent_id: string | null;
  status: OrganizationStatus;
  operates_weekends: boolean;
  operates_holidays: boolean;
  excludes_bridge_days: boolean;
  operating_hours_start: string;
  operating_hours_end: string;
  closed_dates: string[];
}>;

export type OperatingCoverage = {
  organization_id: string;
  from_date: string;
  to_date: string;
  operating_days: number;
  expected_hours: number;
  assigned_hours: number;
  shortfall_hours: number;
  coverage_pct: number;
  closed_days: number;
};

export async function getOperatingCoverage(
  orgId: string,
  from: string,
  to: string,
): Promise<OperatingCoverage> {
  const { data } = await apiClient.get<OperatingCoverage>(
    `/api/v1/organizations/${orgId}/operating-coverage`,
    { params: { from, to } },
  );
  return data;
}

export async function updateOrganization(
  id: string,
  p: UpdateOrganizationPayload,
): Promise<Organization> {
  const { data } = await apiClient.patch<Organization>(`/api/v1/organizations/${id}`, p);
  return data;
}

export async function deleteOrganization(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/organizations/${id}`);
}
